import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import {
  Avatar,
  Box,
  Container,
  Drawer,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import logo from '../assets/field-ops-logo.png';
import { authStore, type DashboardAuth } from '../auth/authStore';
import { useDisplayIdentity } from '../auth/useDisplayIdentity';
import { CLIENT_BRAND_NAME } from '../branding';
import type { DisplayIdentityApi } from '../auth/displayIdentityApi';
import { legalDocuments } from '../legal/legalDocuments';
import { PageHeaderPortalProvider, PageHeaderPortalTarget } from './PageHeaderPortal';
import { getVisibleNavItems, isNavItemActive, type NavItem } from './navItems';

export const DASHBOARD_SHELL_TOKENS = {
  desktopBreakpoint: 1024,
  identityLabelBreakpoint: 640,
  expandedSidebarWidth: 216,
  collapsedSidebarWidth: 68,
  sidebarTransition: 'width 250ms ease',
  sidebarCollapsedStorageKey: 'field-ops.dashboard.sidebar-collapsed',
  colors: {
    shell: '#fcfcfc',
    main: '#ffffff',
    hover: '#f5f5f5',
    primary: '#0c0c0c',
    secondary: '#535353',
    mainBorder: 'rgba(196, 196, 215, 0.25)',
  },
} as const;

const getLocalStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const readDesktopSidebarCollapsed = (): boolean => {
  try {
    const value = getLocalStorage()?.getItem(DASHBOARD_SHELL_TOKENS.sidebarCollapsedStorageKey);
    return value === 'true';
  } catch {
    return false;
  }
};

const persistDesktopSidebarCollapsed = (collapsed: boolean): void => {
  try {
    getLocalStorage()?.setItem(DASHBOARD_SHELL_TOKENS.sidebarCollapsedStorageKey, String(collapsed));
  } catch {
    // A disabled or full browser storage must not prevent navigation from working.
  }
};

const getInitials = (personName: string | null): string | null => {
  if (!personName) return null;

  const initials = personName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]?.toLocaleUpperCase())
    .join('');

  return initials || null;
};

type DashboardNavigationProps = {
  items: readonly NavItem[];
  pathname: string;
  collapsed: boolean;
  onNavigate?: () => void;
  onSignOut: () => void;
};

const DashboardNavigation = ({ items, pathname, collapsed, onNavigate, onSignOut }: DashboardNavigationProps) => (
  <Box component="nav" aria-label="Primary navigation" sx={{ display: 'flex', minHeight: 0, flex: 1, flexDirection: 'column' }}>
    <List disablePadding sx={{ minWidth: 0, minHeight: 0, flex: '1 1 auto', overflowY: 'auto', px: 1, py: 1 }}>
      {items.map(item => {
        const active = isNavItemActive(pathname, item);
        const navigationItem = (
          <ListItemButton
            component={Link}
            to={item.path}
            selected={active}
            aria-current={active ? 'page' : undefined}
            aria-label={collapsed ? item.label : undefined}
            onClick={onNavigate}
            sx={{
              minHeight: 44,
              justifyContent: collapsed ? 'center' : 'flex-start',
              borderRadius: 1.5,
              color: DASHBOARD_SHELL_TOKENS.colors.secondary,
              px: collapsed ? 1 : 1.5,
              '&:hover': { bgcolor: DASHBOARD_SHELL_TOKENS.colors.hover },
              '&.Mui-selected': {
                bgcolor: DASHBOARD_SHELL_TOKENS.colors.main,
                boxShadow: '0 2px 8px rgba(12, 12, 12, 0.08)',
                color: DASHBOARD_SHELL_TOKENS.colors.primary,
              },
              '&.Mui-selected:hover': { bgcolor: DASHBOARD_SHELL_TOKENS.colors.main },
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, color: 'inherit' }}>
              <item.Icon fontSize="small" />
            </ListItemIcon>
            {!collapsed && <ListItemText primary={item.label} sx={{ '& .MuiListItemText-primary': { fontSize: 14, fontWeight: 600 } }} />}
          </ListItemButton>
        );

        return (
          <ListItem key={item.path + item.label} disablePadding sx={{ mb: 0.5 }}>
            {collapsed ? <Tooltip title={item.label} placement="right">{navigationItem}</Tooltip> : navigationItem}
          </ListItem>
        );
      })}
    </List>
    <Box sx={{ mt: 'auto', px: 1, pb: 1 }}>
      <Tooltip title={collapsed ? 'Sign out' : ''} placement="right">
        <ListItemButton
          onClick={onSignOut}
          aria-label={collapsed ? 'Sign out' : undefined}
          sx={{
            minHeight: 44,
            justifyContent: collapsed ? 'center' : 'flex-start',
            borderRadius: 1.5,
            color: DASHBOARD_SHELL_TOKENS.colors.secondary,
            px: collapsed ? 1 : 1.5,
            '&:hover': { bgcolor: DASHBOARD_SHELL_TOKENS.colors.hover },
          }}
        >
          <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, color: 'inherit' }}>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Sign out" sx={{ '& .MuiListItemText-primary': { fontSize: 14, fontWeight: 600 } }} />}
        </ListItemButton>
      </Tooltip>
    </Box>
  </Box>
);

type DisplayIdentityControlProps = {
  auth: DashboardAuth;
  api?: DisplayIdentityApi;
  accountMenuOpen: boolean;
  anchorMenu: (anchor: HTMLElement) => void;
};

const DisplayIdentityControl = ({ auth, api, accountMenuOpen, anchorMenu }: DisplayIdentityControlProps) => {
  const { personName, brokerageName, loading } = useDisplayIdentity({ agentId: auth.agentId, api });
  const initials = getInitials(personName);

  return (
    <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
      <Box
        component="button"
        type="button"
        aria-label="Account menu"
        aria-controls={accountMenuOpen ? 'account-menu' : undefined}
        aria-expanded={accountMenuOpen ? 'true' : undefined}
        aria-haspopup="menu"
        onClick={event => anchorMenu(event.currentTarget)}
        sx={{
          display: 'flex',
          minHeight: 40,
          minWidth: 40,
          maxWidth: '100%',
          alignItems: 'center',
          gap: 1,
          border: 0,
          borderRadius: 1.5,
          bgcolor: 'transparent',
          color: DASHBOARD_SHELL_TOKENS.colors.primary,
          cursor: 'pointer',
          p: 0.5,
          font: 'inherit',
          '&:hover': { bgcolor: DASHBOARD_SHELL_TOKENS.colors.hover },
          '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: 2 },
        }}
      >
        {loading ? (
          <Skeleton data-testid="display-identity-skeleton" variant="rounded" width={148} height={32} />
        ) : (
          <>
            <Avatar sx={{ width: 32, height: 32, bgcolor: DASHBOARD_SHELL_TOKENS.colors.primary, fontSize: 13 }} aria-hidden="true">
              {initials ?? <PersonIcon fontSize="small" />}
            </Avatar>
            {(personName || brokerageName) && (
              <Box
                sx={{
                  minWidth: 0,
                  textAlign: 'left',
                  [`@media (max-width: ${DASHBOARD_SHELL_TOKENS.identityLabelBreakpoint - 0.05}px)`]: { display: 'none' },
                }}
              >
                {personName && (
                  <Typography noWrap variant="body2" sx={{ color: DASHBOARD_SHELL_TOKENS.colors.primary, fontWeight: 600 }}>
                    {personName}
                  </Typography>
                )}
                {brokerageName && (
                  <Typography noWrap variant="caption" sx={{ color: DASHBOARD_SHELL_TOKENS.colors.secondary, display: 'block' }}>
                    {brokerageName}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export type DashboardShellProps = {
  auth: DashboardAuth;
  displayIdentityApi?: DisplayIdentityApi;
};

export const DashboardShell = ({ auth, displayIdentityApi }: DashboardShellProps) => {
  const location = useLocation();
  const isDesktop = useMediaQuery(`(min-width:${DASHBOARD_SHELL_TOKENS.desktopBreakpoint}px)`, { noSsr: true });
  const visibleNavItems = getVisibleNavItems(auth.role);
  const [desktopCollapsed, setDesktopCollapsed] = useState(readDesktopSidebarCollapsed);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);
  const isAccountMenuOpen = accountMenuAnchor !== null;

  useEffect(() => {
    if (isDesktop) setMobileNavigationOpen(false);
  }, [isDesktop]);

  const closeAccountMenu = (): void => setAccountMenuAnchor(null);

  const toggleDesktopSidebar = (): void => {
    setDesktopCollapsed(currentlyCollapsed => {
      const nextCollapsed = !currentlyCollapsed;
      persistDesktopSidebarCollapsed(nextCollapsed);
      return nextCollapsed;
    });
  };

  const signOut = (): void => {
    closeAccountMenu();
    authStore.clearAuth();
  };

  return (
    <PageHeaderPortalProvider>
      <Box
        data-testid="dashboard-shell"
        sx={{
          display: 'flex',
          height: '100dvh',
          minHeight: '100vh',
          overflow: 'hidden',
          bgcolor: DASHBOARD_SHELL_TOKENS.colors.shell,
        }}
      >
        {isDesktop && (
          <Box
            component="aside"
            data-testid="desktop-sidebar"
            sx={{
              display: 'flex',
              width: desktopCollapsed
                ? `${DASHBOARD_SHELL_TOKENS.collapsedSidebarWidth}px`
                : `${DASHBOARD_SHELL_TOKENS.expandedSidebarWidth}px`,
              flex: '0 0 auto',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: DASHBOARD_SHELL_TOKENS.colors.shell,
              transition: DASHBOARD_SHELL_TOKENS.sidebarTransition,
            }}
          >
            <Box sx={{ display: 'flex', minHeight: 64, alignItems: 'center', justifyContent: desktopCollapsed ? 'center' : 'space-between', px: 1.5 }}>
              {desktopCollapsed ? (
                <IconButton aria-label="Expand sidebar" size="small" onClick={toggleDesktopSidebar}>
                  <Box component="img" src={logo} alt={CLIENT_BRAND_NAME} sx={{ width: 32, height: 32 }} />
                </IconButton>
              ) : (
                <>
                  <Box sx={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 1 }}>
                    <Box component="img" data-testid="dashboard-brand" src={logo} alt={CLIENT_BRAND_NAME} sx={{ width: 32, height: 32 }} />
                    <Typography noWrap sx={{ color: DASHBOARD_SHELL_TOKENS.colors.primary, fontSize: 16, fontWeight: 700 }}>
                      {CLIENT_BRAND_NAME}
                    </Typography>
                  </Box>
                  <IconButton aria-label="Collapse sidebar" size="small" onClick={toggleDesktopSidebar}>
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
            <DashboardNavigation
              items={visibleNavItems}
              pathname={location.pathname}
              collapsed={desktopCollapsed}
              onSignOut={signOut}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', minWidth: 0, flex: 1, flexDirection: 'column', bgcolor: DASHBOARD_SHELL_TOKENS.colors.main }}>
          <Box
            component="header"
            data-testid="dashboard-top-bar"
            sx={{
              display: 'flex',
              minHeight: 64,
              flex: '0 0 auto',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              borderBottom: `1px solid ${DASHBOARD_SHELL_TOKENS.colors.mainBorder}`,
              px: { xs: 2, sm: 3, md: 4 },
              py: 1,
            }}
          >
            {!isDesktop && (
              <IconButton aria-label="Open navigation" onClick={() => setMobileNavigationOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
            <PageHeaderPortalTarget data-testid="page-header-target" style={{ flex: '1 1 12rem', minWidth: 0 }} />
            <DisplayIdentityControl
              auth={auth}
              api={displayIdentityApi}
              accountMenuOpen={isAccountMenuOpen}
              anchorMenu={setAccountMenuAnchor}
            />
          </Box>
          <Box
            component="main"
            sx={{
              minWidth: 0,
              flex: 1,
              overflow: 'auto',
              borderLeft: isDesktop ? `1px solid ${DASHBOARD_SHELL_TOKENS.colors.mainBorder}` : 0,
              bgcolor: DASHBOARD_SHELL_TOKENS.colors.main,
            }}
          >
            <Container maxWidth="xl" sx={{ minWidth: 0, py: { xs: 2, sm: 3, md: 4 } }}>
              <Outlet />
            </Container>
          </Box>
        </Box>
        <Drawer
          open={!isDesktop && mobileNavigationOpen}
          onClose={() => setMobileNavigationOpen(false)}
          slotProps={{
            backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.4)' } },
            paper: {
              sx: {
                width: DASHBOARD_SHELL_TOKENS.expandedSidebarWidth,
                bgcolor: DASHBOARD_SHELL_TOKENS.colors.shell,
              },
            },
          }}
        >
          <Box component="aside" aria-label="Mobile navigation" sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', minHeight: 64, alignItems: 'center', gap: 1, px: 2 }}>
              <Box component="img" src={logo} alt={CLIENT_BRAND_NAME} sx={{ width: 32, height: 32 }} />
              <Typography sx={{ color: DASHBOARD_SHELL_TOKENS.colors.primary, fontSize: 16, fontWeight: 700 }}>{CLIENT_BRAND_NAME}</Typography>
            </Box>
            <DashboardNavigation
              items={visibleNavItems}
              pathname={location.pathname}
              collapsed={false}
              onNavigate={() => setMobileNavigationOpen(false)}
              onSignOut={signOut}
            />
          </Box>
        </Drawer>
        <Menu
          id="account-menu"
          anchorEl={accountMenuAnchor}
          open={isAccountMenuOpen}
          onClose={closeAccountMenu}
          slotProps={{ list: { 'aria-label': 'Account menu' } }}
        >
          {auth.role === 'broker' ? (
            <>
              <MenuItem component={Link} to="/terms-and-conditions" onClick={closeAccountMenu}>
                Terms & Conditions
              </MenuItem>
              <MenuItem component={Link} to="/privacy-policy" onClick={closeAccountMenu}>
                Privacy Policy
              </MenuItem>
              <Divider />
              <MenuItem component={Link} to="/core-configuration" onClick={closeAccountMenu}>
                Core Configuration
              </MenuItem>
              <Divider />
            </>
          ) : (
            <>
              {legalDocuments.map(document => (
                <MenuItem key={document.path} component={Link} to={document.path} onClick={closeAccountMenu}>
                  {document.label}
                </MenuItem>
              ))}
              <Divider />
            </>
          )}
          <MenuItem component={Link} to="/about" onClick={closeAccountMenu}>
            About
          </MenuItem>
        </Menu>
      </Box>
    </PageHeaderPortalProvider>
  );
};
